import { c } from 'ttag';

import { refusalCode } from '../../../graphql/refusalCode';
import { Refusal } from '../../accounts/portalAccess';

/**
 * Reading of the refusals `inviteUser` answers with: opened at PCG-85 on the four
 * codes of the contact, widened at PCG-110 to everything the mutation can refuse.
 *
 * The wording is **ours**, as everywhere else in this journey: the API's refusal
 * strings are written for whoever reads a log, and an enumeration is not a message.
 * What each code has to say is **the gesture it calls for**, which is the only reason
 * the API carries twenty of them rather than one `FORBIDDEN`.
 *
 * **Attention, the defect this lot repairs was not a missing sentence but a lying frame.**
 * Four codes were read and the rest fell to the generic branch, which says « rien ici
 * ne dit quoi en faire » and prints the server's answer underneath. On
 * `MEMBERSHIP_ALREADY_EXISTS` (the commonest operator mistake there is) the frame
 * sent the operator to the support desk while the block directly below it read
 * « changez son rôle depuis sa fiche ». A screen that contradicts itself in two
 * consecutive lines is worse than one that says nothing: the reader has no way of
 * knowing which half to believe.
 *
 * ── Four families, and they are not a tidying device
 *
 * They decide the **shape** of the answer, so a new code is placed by asking which
 * one it belongs to:
 *
 * 1. **the contact**: the identity came from a search at step 1, and the world moved
 *    between that search and the send. The gesture is to search again, never to retry;
 * 2. **a state of the data one gesture repairs**: the draft is well formed, something
 *    on the platform says no, and a named gesture lifts it;
 * 3. **the refusal is about the actor, not the draft**: a right or a perimeter. No
 *    gesture of theirs lifts it; the onboarding is handed over;
 * 4. **a draft the screen should never have let compose**: no operator gesture
 *    repairs it and « réessayez » would be a loop, because a second send composes the
 *    very same thing. It is written as what it is: a defect of this journey, to be
 *    reported rather than worked around.
 *
 * ── Why `detail` travels beyond the unknown code here
 *
 * A decision of this lot, and it rests on two facts of the API rather than on taste:
 *
 * - **the account names live in the message and nowhere else.** `formatError` strips
 *   every extension but `code`, so `inviteUser` writes the refused accounts into the
 *   text of the error. A draft carries N attachments and the screen cannot tell which
 *   one was refused: « changez son rôle depuis sa fiche » without naming the account
 *   is an instruction the operator cannot act on;
 * - **only the first code travels.** When several accounts are refused for different
 *   reasons the API keeps `refusals[0].code` and concatenates every message. The
 *   sentence above therefore answers **one** of the refusals, and the block below is
 *   the only place the others appear at all.
 *
 * So the server's sentence is kept wherever the refusal names **an object the front
 * does not hold** (the refused accounts, and since PCG-122 the role or the catalogue
 * entry the draft points at), and dropped where it concerns the person or the session:
 * there it only repeats, in the wrong register, what the guidance already says.
 * Family 4 keeps it unconditionally: its guidance asks for it to be reported.
 *
 * **Attention, the generic branch stays, and it stays true.** A code can appear at any time:
 * this reading was already an undercount of what the mutation throws, and it will be
 * one again. What had to stop was its frame showing over a code that *is* read.
 */

/** The server's own sentence. Kept verbatim: paraphrasing a technical note loses it. */
const serverAnswer = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/**
 * Family 1: the contact resolved at step 1.
 *
 * Attention, two of the three are reached by **the same accident**, and it is worth naming
 * because it decides the copy: the draft resolved a contact at step 1, and the world
 * moved between that search and the send: an ingestion run, someone else onboarding
 * the same person. So « retry » is never the guidance; « search the address again »
 * is, because the answer the screen holds is the stale thing, not the click.
 */
const contactRefusal = (code: string | null): Refusal | null => {
    switch (code) {
        case 'CONTACT_NOT_FOUND':
            return {
                title: c('Onboarding invite refusal').t`This contact no longer exists.`,
                guidance: c('Onboarding invite refusal')
                    .t`It has disappeared from the source since it was picked at step 1. Go back to step 1 and search the address again: what comes back now is what can be onboarded.`,
            };
        case 'CONTACT_EMAIL_MISMATCH':
            // Attention, « The contact does not carry the invited address » is what the API
            // says; what it *means* on this screen is that the address moved after the
            // search. The contact is not the thing to correct: the identity comes from
            // the source and is never typed here, so the gesture is to search again,
            // not to go and fix a record that is right.
            return {
                title: c('Onboarding invite refusal').t`The address being invited is not the one this contact carries.`,
                guidance: c('Onboarding invite refusal')
                    .t`In practice the address changed after the search, so the identity on this journey belongs to someone else. Go back to step 1, search the address as it stands now, and pick the contact it designates. There is nothing to correct on the contact itself: the identity comes from the source.`,
            };
        case 'CONTACT_ALREADY_LINKED':
            // Attention, the one refusal of this screen that **no gesture repairs**, and it is
            // written as such. Offering « recherchez à nouveau » would be a loop: the
            // search will keep returning the same contact, and the contact will keep
            // pointing at somebody else until the source is corrected. A refusal that
            // suggests an action which cannot work is worse than one that says plainly
            // there is none.
            return {
                title: c('Onboarding invite refusal').t`This contact already designates another portal user.`,
                guidance: c('Onboarding invite refusal')
                    .t`A contact is onboarded once, and this one was onboarded for somebody else — which happens when the source changes the address of a contact already onboarded. Nothing on this screen repairs it: searching again will return the same contact. Have the source corrected, then start the onboarding over.`,
            };
        default:
            return null;
    }
};

/**
 * Family 2: a state of the data, and one gesture that lifts it.
 *
 * Every entry names **where** the gesture is made, because none of them is made here:
 * the person's own sheet, the account panel, or a step to go back to. A refusal that
 * says what is true without saying where to go leaves the operator on this screen with
 * a disabled path.
 */
const dataStateRefusal = (code: string | null, server: string): Refusal | null => {
    switch (code) {
        case 'MEMBERSHIP_ALREADY_EXISTS':
            // **Attention, the one that shipped the defect.** It is the commonest mistake of
            // this journey (an operator reinviting somebody to add a role) and it was
            // answered by « ce refus n'est pas un refus que l'écran sait lire ».
            return {
                title: c('Onboarding invite refusal')
                    .t`This person is already attached to one of the selected accounts.`,
                guidance: c('Onboarding invite refusal')
                    .t`An invitation adds attachments and never rewrites the ones already there, so the role and the perimeter of an existing attachment are changed from the person's own sheet rather than from here. Take that account out of the selection at step 2 and send again — or, if it was the only one, there is nothing left to invite. The accounts concerned are named below.`,
                detail: server,
            };
        case 'PORTAL_CLOSED_ON_ACCOUNT':
            // Attention, structural rather than accidental: nearly every account is closed to
            // the portal today, so this is the ordinary answer and not an incident. The
            // copy says so, otherwise the operator reads their own selection as wrong.
            return {
                title: c('Onboarding invite refusal').t`One of the selected accounts is not open to the portal.`,
                guidance: c('Onboarding invite refusal')
                    .t`Attaching somebody to a closed account would open an access onto nothing. Opening an account to the portal is a separate gesture, taken on the account itself, and it comes first — otherwise take that account out of the selection at step 2. Most accounts are closed today, so this answer is the ordinary one rather than a sign that something is broken. The accounts concerned are named below.`,
                detail: server,
            };
        case 'DIFFERENT_ROOT_ORGANIZATION':
            return {
                title: c('Onboarding invite refusal').t`These accounts do not belong to the same client.`,
                guidance: c('Onboarding invite refusal')
                    .t`A person on the client side belongs to a single client — the root organisation — and may be attached to several accounts of its tree, never across two. Either the selection at step 2 mixes two clients, or this person already holds an access at another client which has to be closed before one is opened here. The server's answer below names what is in the way.`,
                detail: server,
            };
        case 'MARKET_RESTRICTION_INCOMPATIBLE':
            return {
                title: c('Onboarding invite refusal')
                    .t`A perimeter restricted to a few markets cannot hold on one of these accounts.`,
                guidance: c('Onboarding invite refusal')
                    .t`A module opened on that account cannot honour a restriction by market: part of what it lists carries no market at all, so those lists would silently lose rows. Set that line back to « the whole account » at step 2, or have the module closed on the account first. The account and the modules in the way are named below.`,
                detail: server,
            };
        case 'ORGANIZATION_NOT_FOUND':
            // **Attention, it names an account again, and only an account.** Until PCG-121 the
            // API answered this same code for a dashboard withdrawn from the catalogue,
            // so the title could name neither object without being wrong half the time
            // and said « something ». The dashboard now answers under its own two codes
            // below, which is what lets this one be said plainly. The server's sentence
            // stays: a draft carries N accounts and only the refused one is named there.
            return {
                title: c('Onboarding invite refusal').t`One of the selected accounts no longer exists.`,
                guidance: c('Onboarding invite refusal')
                    .t`It has gone from the source since it was picked, and an attachment cannot hang from an account that is no longer there. Reload the page and compose the selection again from what it offers now. The account concerned is named below.`,
                detail: server,
            };
        case 'DASHBOARD_NOT_FOUND':
            // **Attention, two codes for the dashboards, and they are not one refusal said
            // twice.** Withdrawing an entry archives it, so « no entry carries this
            // identifier » is the rarer accident: the row itself has gone, nothing is
            // named, and nothing can be put back. That is what separates it from
            // `DASHBOARD_ARCHIVED` below, where an entry exists, carries a name, and one
            // gesture on the catalogue brings it back. One sentence for the two would
            // promise a repair on the half that has none.
            return {
                title: c('Onboarding invite refusal').t`A dashboard picked at step 3 is not in the catalogue at all.`,
                guidance: c('Onboarding invite refusal')
                    .t`No entry carries the identifier this draft points at. Withdrawing an entry archives it rather than deleting it, so this is the rarer case: the entry itself has gone, and there is nothing to have put back. Go back to step 3, where the catalogue is served again, and pick from what it offers now. This step is optional: inviting without any dashboard is an ordinary outcome. The identifier refused is in the answer below.`,
                detail: server,
            };
        case 'DASHBOARD_ARCHIVED':
            return {
                title: c('Onboarding invite refusal')
                    .t`A dashboard picked at step 3 has been withdrawn from the catalogue.`,
                guidance: c('Onboarding invite refusal')
                    .t`Somebody archived it since step 3 was left, and an archived entry is no longer grantable. Two ways out: have it put back in the catalogue of its account, which today means declaring it again since no screen un-archives an entry, or go back to step 3 and pick another one. This step is optional: inviting without any dashboard is an ordinary outcome. The entry is named below.`,
                detail: server,
            };
        case 'PARTNER_LIST_NOT_FOUND':
            return {
                title: c('Onboarding invite refusal').t`The partner list picked at step 4 has left the catalogue.`,
                guidance: c('Onboarding invite refusal')
                    .t`Somebody has archived it since step 4 was left, and an archived list is no longer assignable. Go back to step 4 and pick another one — or none at all, attributing no list being a normal outcome of this journey.`,
            };
        case 'ACCOUNT_DISABLED':
            return {
                title: c('Onboarding invite refusal').t`This person's portal account is disabled.`,
                guidance: c('Onboarding invite refusal')
                    .t`They already exist on the portal and their account was disabled, so no attachment is added and no activation link would lead anywhere. Reactivating a person is a separate gesture, taken from their sheet, and it comes first.`,
            };
        case 'PRIVILEGE_ESCALATION':
            return {
                title: c('Onboarding invite refusal').t`One of the roles picked goes beyond your own rights.`,
                guidance: c('Onboarding invite refusal')
                    .t`A role is granted only up to what you hold yourself. The selector at step 2 already disables the ones you cannot grant, so reaching this refusal means your own membership changed while this journey was open. Go back to step 2 and pick a role that is still offered. The account concerned is named below.`,
                detail: server,
            };
        case 'ROLE_NOT_FOUND':
            // **Attention, family 2, and the temptation is to file it under family 3.** It
            // travelled as `MISSING_PERMISSION` until PCG-121, which sent the operator
            // asking for a right that would have unblocked nothing: the refusal is not
            // about them at all, it is about a referential that moved. What repairs it
            // is a gesture of theirs, on this journey, which is what family 2 means.
            return {
                title: c('Onboarding invite refusal').t`The role picked at step 2 no longer exists.`,
                guidance: c('Onboarding invite refusal')
                    .t`It has left the referential since it was picked, and nothing brings a deleted role back. Go back to step 2 and pick another one. Beware that the list was loaded when this journey opened and still offers the one that has gone: picking it again would refuse again. The identifier of the missing role is in the answer below.`,
                detail: server,
            };
        default:
            return null;
    }
};

/**
 * Family 3: the refusal is about the actor, not about the draft.
 *
 * Attention, rendered after the click, and it cannot be otherwise: `viewer` exposes the role
 * but not the permissions behind it, so no screen can know beforehand whether the
 * actor holds `manage_users` on the accounts they picked.
 */
const actorRefusal = (code: string | null, server: string): Refusal | null => {
    switch (code) {
        case 'FORBIDDEN_OUT_OF_SCOPE':
            // **Attention, three different objects answer with this code**: an account of the
            // selection, the contact picked at step 1, and a dashboard hanging from an
            // account that has left the selection. The wording covers the three rather
            // than naming the contact alone, which is what it did until PCG-110 and was
            // wrong two times out of three.
            return {
                title: c('Onboarding invite refusal').t`Part of this invitation is outside your portfolio.`,
                guidance: c('Onboarding invite refusal')
                    .t`The same answer covers three things: an account of the selection that is not entrusted to you, the contact picked at step 1, or a dashboard hanging from an account that has left the selection. The answer below says which. Ask for the account to be added to your portfolio, or hand the onboarding over to whoever administers it.`,
                detail: server,
            };
        case 'MISSING_PERMISSION':
            return {
                title: c('Onboarding invite refusal')
                    .t`Your role does not let you manage the users of one of these accounts.`,
                guidance: c('Onboarding invite refusal')
                    .t`Inviting somebody onto an account requires the right to manage its users, and this screen cannot know beforehand whether you hold it — which is why the button was offered. Ask for that right, or hand the onboarding over to whoever administers the account. The account concerned is named below.`,
                detail: server,
            };
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Onboarding invite refusal').t`Your session no longer carries an active membership.`,
                guidance: c('Onboarding invite refusal')
                    .t`Sign in again, then start the journey over. The draft lives on this screen alone, so note the accounts, the roles and the perimeters picked before leaving: they are not saved anywhere.`,
            };
        default:
            return null;
    }
};

/**
 * Family 4: a draft the screen should never have let compose.
 *
 * **Attention, none of these is an operator's mistake, and none is repaired by a gesture of
 * theirs.** Every one of them is refused by step 2 already, or cannot be expressed by
 * its controls at all: the account picker excludes what is selected, changing a
 * perimeter clears the markets it carried, the market list only offers the account's
 * own, the role list only offers portal roles, and `stepBlocker` re-runs the lot on the
 * review. So reaching one of them means a guard did not hold, and telling the operator
 * to try again would be a loop, a second send composing the very same draft.
 *
 * They therefore share one guidance: what is true of all four is that there is nothing
 * to do here and something to report. The title is what differs, because « the same
 * account twice » and « a back-office role » are not the same defect to report.
 */
const malformedDraftRefusal = (code: string | null, server: string): Refusal | null => {
    const title = ((): string | null => {
        switch (code) {
            case 'DUPLICATE_ATTACHMENT':
                return c('Onboarding invite refusal').t`The same account appears twice in the selection.`;
            case 'NO_TARGET_ORGANIZATION':
                return c('Onboarding invite refusal').t`This invitation carries no account at all.`;
            case 'UNKNOWN_MARKET_SCOPE':
                return c('Onboarding invite refusal').t`A line carries a perimeter the platform does not know.`;
            case 'MARKETS_WITH_UNRESTRICTED_SCOPE':
                return c('Onboarding invite refusal').t`A line lists markets while its perimeter is the whole account.`;
            case 'EMPTY_MARKET_RESTRICTION':
                return c('Onboarding invite refusal').t`A line restricts the perimeter to no market at all.`;
            case 'MARKET_NOT_IN_SCOPE':
                return c('Onboarding invite refusal').t`A line lists markets that do not belong to its account.`;
            case 'BACKOFFICE_ROLE_NOT_ALLOWED':
                return c('Onboarding invite refusal').t`A back-office role was picked on a client account.`;
            default:
                return null;
        }
    })();
    if (!title) {
        return null;
    }
    return {
        title,
        guidance: c('Onboarding invite refusal')
            .t`This is a defect of the journey rather than a mistake of yours: the screen let a draft be composed that its own checks should have refused. Sending again would refuse again, since it would compose the same thing. Everything travels in one transaction, so nothing has been created and nothing has to be cleaned up — start the onboarding over, and report the answer below, which is what identifies the defect.`,
        detail: server,
    };
};

/** The refusal, told to the operator who has to unblock it. */
export const inviteRefusalOf = (error: unknown): Refusal => {
    const code = refusalCode(error);
    const server = serverAnswer(error);
    const known =
        contactRefusal(code) ??
        dataStateRefusal(code, server) ??
        actorRefusal(code, server) ??
        malformedDraftRefusal(code, server);
    if (known) {
        return known;
    }
    return {
        title: c('Onboarding invite refusal').t`The invitation was refused. Nothing was created.`,
        guidance: c('Onboarding invite refusal')
            .t`This refusal is not one the screen knows how to read, so nothing here says what to do about it. Everything is sent in a single transaction, so the person has not been half-created — you can safely start over. The server's answer is below: it is what the support desk needs.`,
        // The server's own sentence, on a code we could not read. An unknown code with
        // no detail leaves the operator with nothing to report.
        detail: server,
    };
};
